import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './beelineLp.css';
import ApiSender from '../common/ApiSender';
import { toNationalNumber, sanitizeMsisdnInput } from '../common/msisdn';
import { isBeeline, resultPath } from '../common/carrier';
import { isFailedSubscribe } from '../common/failureRouting';
import { FAILED_ICON } from '../common/failedIcon';
import { content, LANGS, DEFAULT_LANG } from './content';

const BEELINE_OPERATOR_ID = 9;
const RETRY_COOLDOWN_MS = 4000;
const LANG_KEY = 'beelineLpLang';

// ---------- language ----------

function initialLang(search) {
  const fromUrl = new URLSearchParams(search).get('lang');
  if (fromUrl && content[fromUrl]) return fromUrl;
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored && content[stored]) return stored;
  } catch (e) {
    // storage unavailable: fall back to the default
  }
  return DEFAULT_LANG;
}

export function useLang() {
  const { search } = useLocation();
  const [lang, setLangState] = useState(() => initialLang(search));
  const setLang = (code) => {
    setLangState(code);
    try {
      localStorage.setItem(LANG_KEY, code);
    } catch (e) {
      // ignore
    }
  };
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = content[lang].meta.title;
  }, [lang]);
  return [lang, setLang, content[lang]];
}

// ---------- small pieces ----------

// Yellow/black striped badge in the spirit of the Beeline mark (no brand asset is bundled).
function LangSwitch({ lang, setLang }) {
  return (
    <div className="bl-lang" role="group" aria-label="Language">
      {LANGS.map((l) => (
        <button key={l.code} type="button" title={l.label} className={l.code === lang ? 'is-active' : ''} onClick={() => setLang(l.code)}>
          {l.short}
        </button>
      ))}
    </div>
  );
}


function useCampaign(lpPath) {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const apiSender = useMemo(() => new ApiSender(), []);
  const [goal, setGoal] = useState({ id: null, name: null });

  useEffect(() => {
    if (!params.has('campaignId')) return;
    fetch(process.env.REACT_APP_GOOGLE_BASE_URL + 'campaign/info?uuid=' + params.get('campaignId'), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && data.status) setGoal({ id: data.goalId, name: data.goalName });
      })
      .catch(() => {});
  }, [params]);

  useEffect(() => {
    apiSender.saveHistory(window.location.origin + lpPath, Object.fromEntries(params.entries()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { params, apiSender, goal };
}

function deviceFingerprint() {
  const fmt = (v) => (v === null || v === undefined ? '' : String(v).replaceAll(',', ''));
  return [
    navigator.hardwareConcurrency ?? null,
    navigator.language,
    navigator.deviceMemory ?? null,
    `${window.screen.width}x${window.screen.height}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.userAgent,
  ].map(fmt).join(',');
}

function SubscribeForm({ lpKey, lpName, t, campaign, inputRef, compact }) {
  const nav = useNavigate();
  const { params, apiSender, goal } = campaign;
  const [msisdn, setMsisdn] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const subscribedKey = `${lpKey}_subscribed`;

  const wasSubscribed = (n) => {
    try {
      return JSON.parse(sessionStorage.getItem(subscribedKey) || '[]').includes(n);
    } catch (e) {
      return false;
    }
  };
  const rememberSubscribed = (n) => {
    try {
      const list = JSON.parse(sessionStorage.getItem(subscribedKey) || '[]');
      if (!list.includes(n)) sessionStorage.setItem(subscribedKey, JSON.stringify([...list, n]));
    } catch (e) {
      // ignore
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const national = toNationalNumber(msisdn);
    if (national === null) return setError(t.form.errInvalid);
    if (!isBeeline(msisdn)) return setError(t.form.errNotBeeline);

    setError('');
    setBusy(true);
    const g = (k) => params.get(k) || null;
    const result = await apiSender.subscribe(
      'subscribe', national, g('campaignId'), 'RU', g('trackerId'), g('gclid'), g('gbraid'), g('wbraid'),
      goal.id, goal.name, window.location.origin + `/${lpKey}`, lpName, BEELINE_OPERATOR_ID,
      deviceFingerprint(), g('yclid'), g('client_id'), g('user_id'),
    );

    // The engine answers HTTP 500 (not "already subscribed") when a subscribed number is sent again.
    if (result.success || (result.httpStatus === 500 && wasSubscribed(national))) {
      if (result.success) rememberSubscribed(national);
      nav(resultPath(lpKey, 'beeline', 'success'));
      return;
    }
    if (/already subscribed/i.test(result.message ?? '')) {
      setBusy(false);
      return setError(t.form.errAlready);
    }
    if (isFailedSubscribe(result)) {
      nav(resultPath(lpKey, 'beeline', 'failed'));
      return;
    }
    setError(t.form.errGeneric);
    setTimeout(() => setBusy(false), RETRY_COOLDOWN_MS);
  };

  return (
    <form className={`bl-form ${compact ? 'bl-form--compact' : ''}`} onSubmit={submit} noValidate>
      <label className="bl-form__label" htmlFor={`${lpKey}-msisdn`}>{t.form.phoneTitle}</label>
      <div className={`bl-input ${error ? 'has-error' : ''}`}>
        <span className="bl-input__cc">+996</span>
        <input
          id={`${lpKey}-msisdn`}
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder={t.form.placeholder}
          value={msisdn}
          maxLength={10}
          onChange={(ev) => { setMsisdn(sanitizeMsisdnInput(ev.target.value)); setError(''); }}
          aria-invalid={!!error}
          aria-describedby={`${lpKey}-msisdn-msg`}
        />
      </div>
      <p id={`${lpKey}-msisdn-msg`} className="bl-form__msg" role="alert">{error}</p>
      <button type="submit" className="bl-btn bl-btn--primary bl-btn--block" disabled={busy}>
        {busy ? t.form.loading : t.form.button}
      </button>
    </form>
  );
}


const Icon = {
  activate: (
    <svg viewBox="0 0 64 64" aria-hidden="true"><rect x="18" y="6" width="28" height="52" rx="6" fill="#fff" stroke="#111" strokeWidth="3" /><circle cx="32" cy="30" r="11" fill="#FFD21E" stroke="#111" strokeWidth="3" /><path d="M26.5 30.5l4 4 7-8" fill="none" stroke="#111" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" /><path d="M28 51h8" stroke="#111" strokeWidth="3" strokeLinecap="round" /></svg>
  ),
  download: (
    <svg viewBox="0 0 64 64" aria-hidden="true"><circle cx="32" cy="32" r="26" fill="#FFD21E" stroke="#111" strokeWidth="3" /><path d="M32 17v22M22 31l10 10 10-10" fill="none" stroke="#111" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" /><path d="M20 46h24" stroke="#111" strokeWidth="4" strokeLinecap="round" /></svg>
  ),
  pin: (
    <svg viewBox="0 0 64 64" aria-hidden="true"><rect x="6" y="16" width="52" height="32" rx="8" fill="#fff" stroke="#111" strokeWidth="3" /><g fill="#111"><circle cx="20" cy="32" r="3.5" /><circle cx="32" cy="32" r="3.5" /><circle cx="44" cy="32" r="3.5" /></g><circle cx="52" cy="14" r="9" fill="#FFD21E" stroke="#111" strokeWidth="3" /><path d="M48 14l3 3 5-6" fill="none" stroke="#111" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
  ),
};

function KidSecurityLogo() {
  return <img className="bl-logo" src="/kid-security-logo.png" alt="Kid Security" />;
}

function Header({ lang, setLang }) {
  return (
    <header className="bl-header">
      <div className="bl-wrap bl-header__row">
        <a href="#top" className="bl-header__logo"><KidSecurityLogo /></a>
        <LangSwitch lang={lang} setLang={setLang} />
      </div>
    </header>
  );
}


// Generic hero artwork: a phone showing a successful activation, with small floating feature badges.
function HeroVisual({ variant }) {
  const dark = variant === 2;
  const frame = dark ? '#FFD21E' : '#111';
  const screen = dark ? '#1c1c1c' : '#fff';
  const ring = dark ? '#2a2a2a' : '#fff3b0';
  const line = dark ? '#3a3a3a' : '#ececec';
  const badge = (x, y, children) => (
    <g transform={`translate(${x} ${y})`}>
      <circle r="30" fill="#fff" stroke="#111" strokeWidth="3" />
      {children}
    </g>
  );
  return (
    <svg className="bl-hero__art" viewBox="0 0 400 380" role="img" aria-label="" focusable="false">
      <circle cx="200" cy="190" r="165" fill={ring} />
      {/* phone */}
      <rect x="125" y="30" width="150" height="310" rx="28" fill={frame} />
      <rect x="135" y="42" width="130" height="286" rx="20" fill={screen} />
      <rect x="175" y="48" width="50" height="8" rx="4" fill={frame} />
      <circle cx="200" cy="140" r="46" fill="#22b35a" />
      <path d="M178 141l16 16 30-34" fill="none" stroke="#fff" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="155" y="212" width="90" height="10" rx="5" fill={dark ? '#fff' : '#111'} />
      <rect x="170" y="234" width="60" height="8" rx="4" fill={line} />
      <rect x="155" y="276" width="90" height="30" rx="15" fill="#FFD21E" />
      <rect x="178" y="288" width="44" height="7" rx="3.5" fill="#111" />
      {/* floating badges */}
      {badge(86, 96, <path d="M0-13a9 9 0 0 1 9 9c0 7-9 17-9 17s-9-10-9-17a9 9 0 0 1 9-9zm0 5a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" fill="#e53935" transform="translate(0 -2)" />)}
      {badge(318, 150, <path d="M0-16l14 5v9c0 9-6 15-14 19-8-4-14-10-14-19v-9z" fill="#FFD21E" stroke="#111" strokeWidth="2.5" strokeLinejoin="round" />)}
      {badge(96, 266, <><path d="M0-13v20M-9 0l9 9 9-9" fill="none" stroke="#111" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" /><path d="M-12 15h24" stroke="#111" strokeWidth="4" strokeLinecap="round" /></>)}
      {badge(312, 292, <><circle r="12" fill="#22b35a" /><path d="M-6 0l4 4 8-9" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" /></>)}
    </svg>
  );
}

function Features({ t }) {
  return (
    <ul className="bl-features">
      {t.parent.items.map((it) => (
        <li key={it.title}><img src={it.icon} alt="" /><span>{it.title}</span></li>
      ))}
    </ul>
  );
}

function Steps({ t }) {
  const steps = [[Icon.activate, t.flow[0]], [Icon.download, t.flow[1]], [Icon.pin, t.flow[2]]];
  return (
    <section className="bl-section">
      <div className="bl-wrap">
        <h2 className="bl-title">{t.steps.title}</h2>
        <ol className="bl-flow">
          {steps.map(([icon, s], i) => (
            <li key={s.title}>
              <div className="bl-flow__icon">{icon}<b>{i + 1}</b></div>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Apps({ t }) {
  return (
    <div className="bl-apps__row">
      <div className="bl-app"><img src="/KidsSecurityLogo.png" alt="Kid Security" /><div><b>{t.apps.parentName}</b><span>{t.apps.parentRole}</span></div></div>
      <div className="bl-app"><img src="/TigrowLogo.png" alt="Tigrow" /><div><b>{t.apps.kidName}</b><span>{t.apps.kidRole}</span></div></div>
    </div>
  );
}

/**
 * One short page: the number box is the first thing on screen, then a three-step flow.
 * variant 1: yellow hero, image beside the form
 * variant 2: dark hero, form in a white card
 * variant 3: centred light hero
 */
export default function BeelineLanding({ lpKey, lpName, variant }) {
  const [lang, setLang, t] = useLang();
  const campaign = useCampaign(`/${lpKey}`);
  const inputRef = useRef(null);
  const toForm = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 400);
  };

  return (
    <div className={`bl bl--v${variant}`}>
      <Header lang={lang} setLang={setLang} />
      <section className="bl-hero" id="top">
        <div className="bl-wrap bl-hero__grid">
          <div className="bl-hero__main">
            <span className="bl-pill">{t.hero.badge} · {t.form.from}</span>
            <h1>{t.hero.title}</h1>
            <div className="bl-hero__card" id="subscribe">
              <SubscribeForm lpKey={lpKey} lpName={lpName} t={t} campaign={campaign} inputRef={inputRef} />
            </div>
          </div>
          <HeroVisual variant={variant} />
        </div>
      </section>
      <Steps t={t} />
      <section className="bl-section bl-section--tint">
        <div className="bl-wrap">
          <Features t={t} />
          <Apps t={t} />
          <div className="bl-cta"><button type="button" className="bl-btn bl-btn--primary bl-btn--lg" onClick={toForm}>{t.form.button}</button></div>
        </div>
      </section>
    </div>
  );
}

export function BeelineResult({ lpKey, outcome, variant }) {
  const [lang, setLang, t] = useLang();
  const ok = outcome === 'success';
  return (
    <div className={`bl bl--v${variant} bl-result`}>
      <Header lang={lang} setLang={setLang} />
      <main className="bl-result__card">
        {ok ? <div className="bl-result__ok" aria-hidden="true">✓</div> : <img src={FAILED_ICON} alt="" width="84" height="84" />}
        <h1>{ok ? t.result.successTitle : t.result.failedTitle}</h1>
        <p>{ok ? t.result.successText : t.result.failedText}</p>
        {ok ? <Apps t={t} /> : <a className="bl-btn bl-btn--dark" href={`/${lpKey}`}>{t.result.back}</a>}
      </main>
    </div>
  );
}
